'use client';

import { Wifi, Clock, AlertTriangle } from 'lucide-react';
import PopupTemplate from '@/app/ui/popupTemplate';
import { useTranslations } from 'next-intl';

interface ConnectionIssuePopupProps {
  isOpen: boolean;
  onClose: () => void;
  type?: 'connection_issue' | 'inactivity_timeout';
  inactivityReason?: 'server_signal' | 'user_inactivity' | null;
}

export default function ConnectionIssuePopup({ 
  isOpen, 
  onClose, 
  type = 'connection_issue',
  inactivityReason = 'user_inactivity'
}: ConnectionIssuePopupProps) {
  const t = useTranslations('popup.connectionIssue');
  
  const getTitle = () => {
    if (type === 'inactivity_timeout') {
      return t('videoChatPaused');
    }
    return t('connectionLost');
  };

  const getMessage = () => {
    if (type === 'inactivity_timeout') {
      if (inactivityReason === 'server_signal') {
        return t('pausedDueToInactivity');
      } else {
        return t('pausedDueToInactivityLaunch');
      }
    }
    return t('connectionLostMessage');
  };

  const getIcon = () => {
    if (type === 'inactivity_timeout') {
      return <Clock className="h-12 w-12 text-orange-500" />;
    }
    return <Wifi className="h-12 w-12 text-red-500" />;
  };

  return (
    <PopupTemplate
      isOpen={isOpen}
      onClose={onClose}
      closeable={true}
      isTransparent={false}
    >
      <div className="p-8">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="flex justify-center mb-4">
            {getIcon()}
          </div>
          <h1 className="text-3xl font-bold text-white mb-3">{getTitle()}</h1>
          <p className="text-gray-300 text-sm leading-relaxed">
            {getMessage()}
          </p>
        </div>

        {/* Action button */}
        <div className="space-y-4">
          <button
            onClick={onClose}
            className="w-full flex items-center justify-center gap-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-3 px-4 rounded-xl transition-all duration-200 transform hover:scale-[1.02] hover:shadow-lg"
          >
            <span>{t('close')}</span>
          </button>
        </div>
      </div>
    </PopupTemplate>
  );
} 